import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { format, subDays } from "date-fns";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../api/axios";
import tracker from "../../utils/sanctuary-tracker";
import { MOOD_SCORES, COLORS } from "../../lib/constants";

export default function MoodGraph({ refreshTrigger }) {
    const { user } = useAuth();
    const { t } = useLanguage();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState(7); // 7 or 30

    useEffect(() => {
        if (!user) {
            // 🧘 Guest Support: Load from local storage
            const guestMoods = JSON.parse(localStorage.getItem('ayi_guest_moods') || '[]');
            const processed = guestMoods.slice(-range).map(m => ({
                day: format(new Date(m.createdAt), "dd MMM"),
                mood: m.mood,
                score: m.score || MOOD_SCORES[m.mood] || 3,
                color: COLORS[m.mood] || "#9ca3af"
            }));
            setData(processed);
            setLoading(false);
            return;
        }

        api.get("/moods/mine")
            .then(res => {
                const moods = res.data;
                if (!Array.isArray(moods)) {
                    setData([]);
                    setLoading(false);
                    return;
                }
                const processed = moods.slice(-range).map(m => ({
                    day: format(new Date(m.createdAt), range === 7 ? "EEE" : "dd MMM"),
                    mood: m.mood,
                    score: m.score || MOOD_SCORES[m.mood] || 3,
                    color: COLORS[m.mood] || "#9ca3af"
                }));
                setData(processed);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to load mood graph", err);
                setLoading(false);
                setData([]);
            });
    }, [user, refreshTrigger, range]);

    if (loading) return <div className="h-48 flex items-center justify-center text-xs text-gray-400">{t('dash_mood_graph_loading') || "Loading graph..."}</div>;
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 p-2 shadow-lg rounded-lg border border-gray-100 dark:border-gray-700 text-xs">
                    <p className="font-semibold">{payload[0].payload.day}</p>
                    <p style={{ color: payload[0].payload.color }}>{t('mood_' + payload[0].payload.mood.toLowerCase()) || payload[0].payload.mood}</p>
                </div>
            );
        }
        return null;
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full min-h-[14rem] bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col relative overflow-hidden"
        >
            <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300">{t('pc_title')}</h3>
                <div className="flex bg-gray-100 dark:bg-gray-900 p-0.5 rounded-lg">
                    <button 
                        onClick={() => setRange(7)}
                        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${range === 7 ? 'bg-white dark:bg-gray-800 shadow-sm text-brand-blue' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        7D
                    </button>
                    <button 
                        onClick={() => setRange(30)}
                        className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${range === 30 ? 'bg-white dark:bg-gray-800 shadow-sm text-brand-blue' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        30D
                    </button>
                </div>
            </div>

            <div className="flex-1 w-full mt-2 min-w-0 overflow-x-auto custom-scrollbar">
                <div style={{ minWidth: range === 30 ? '500px' : '100%' }}>
                    {data.length === 0 ? (
                        <div className="h-32 flex items-center justify-center text-xs text-gray-400 p-4 text-center">
                            {t('dash_mood_graph_empty')}
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={120}>
                            <BarChart data={data} barSize={range === 7 ? 24 : 6} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                                <XAxis 
                                    dataKey="day" 
                                    axisLine={false} 
                                    tickLine={false} 
                                    tick={{ fill: '#9ca3af', fontSize: 9 }}
                                    interval={range === 30 ? 6 : 0} // Show label every 7 days for 30D
                                />
                                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                                <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                                    {data.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
